<script lang="ts">
import type { AnyItem, CompositePrimaryKey, FieldSelection, Filter, GlobalPrimitiveFieldId } from '#layers/data-model';
import type { CalendarDisplayOptions, SharedLayoutEmits, SharedLayoutProps } from '~/types/layout';
import { Temporal } from 'temporal-polyfill';
import { getMonthRangeForDate } from '#layers/base';

export type ScheduleXViewId = 'month-grid' | 'week' | 'day' | 'list';

// Schedule-X uses globalThis.Temporal when available (native browser Temporal).
// PlainDate must use the same constructor so Schedule-X's instanceof checks pass.
export const ScheduleXPlainDate = ((globalThis as any).Temporal?.PlainDate ?? Temporal.PlainDate) as typeof Temporal.PlainDate;

export interface LayoutCalendarProps extends SharedLayoutProps<CalendarDisplayOptions> {}

export interface LayoutCalendarEmits extends SharedLayoutEmits {
	'open': [key: CompositePrimaryKey];
	'update:displayOptions': [displayOptions: Record<string, unknown>];
}
</script>

<script setup lang="ts">
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import {
	createCalendar,
	createViewDay,
	createViewList,
	createViewMonthGrid,
	createViewWeek,
} from '@schedule-x/calendar';
import { createCalendarControlsPlugin } from '@schedule-x/calendar-controls';
import { ScheduleXCalendar } from '@schedule-x/vue';
import { createReusableTemplate, whenever } from '@vueuse/core';
import { invert } from 'lodash-es';
import { computed, ref, shallowRef, toRefs, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEmptyFilter, isPrimitiveTypeDateTime } from '#layers/data-model';
import { getItemFieldValue } from '#layers/data-model/utils/item';
import { getItemPrimaryKey, getItemPrimaryKeyRouteString, stringifyPrimaryKey } from '#layers/data-model/utils/primary-key';
import { injectListingContext } from '~/components/Listing/Listing.vue';
import { exposeListingQueryToBlockRootContext } from '~/composables/block-context';
import { useDisplayTemplateFieldSelection } from '~/composables/use-display-template-field-selection';
import { useFieldSelectionForFieldIds } from '~/composables/use-field-selection-for-field-ids';
import { useItems } from '~/composables/use-items';
import { usePrimitiveField } from '~/composables/use-primitive-field';
import { WEEKDAY_SHORT_LABEL_KEYS } from '~/constants/calendar';
import { mergeFieldSelections } from '~/utils/merge-field-selection';
import { resolveDisplayTemplatePlainText } from '~/utils/resolve-display-template-to-plain-text';
import LayoutCalendarDisplayOptions, { getLocaleFirstDayOfWeek, initializeLayoutCalendarOptions } from './LayoutCalendarDisplayOptions.vue';
import LayoutCalendarEventContent from './LayoutCalendarEventContent.vue';

const props = defineProps<LayoutCalendarProps>();
const emit = defineEmits<LayoutCalendarEmits>();

const { collection, filter } = toRefs(props);

const { setDisplayOptionsComponent } = injectListingContext();
setDisplayOptionsComponent(LayoutCalendarDisplayOptions);

const displayOptions = computed(() =>
	initializeLayoutCalendarOptions({ collection: props.collection, options: props.displayOptions }),
);

const startField = usePrimitiveField(() => displayOptions.value.startDateField);
const endField = usePrimitiveField(() => displayOptions.value.endDateField);
const titleTemplate = computed(() => displayOptions.value.titleTemplate);

// Seed the first data fetch from the persisted slice (falls back to the current
// month) so the initial query matches the month the calendar restores to.
const currentViewRange = ref(getMonthRangeForDate(displayOptions.value.currentDate));

const mergedFilter = computed<Filter>(() => {
	if (!startField.value) return filter.value;

	// Datetime columns require full ISO strings; date columns accept YYYY-MM-DD
	const isDateTime = isPrimitiveTypeDateTime(startField.value.type);
	const gte = isDateTime ? `${currentViewRange.value.start}T00:00:00` : currentViewRange.value.start;
	const lte = isDateTime ? `${currentViewRange.value.end}T23:59:59` : currentViewRange.value.end;

	const rangeFilter = {
		[startField.value.apiName]: { _gte: gte, _lte: lte },
	} as Filter;
	if (isEmptyFilter(filter.value)) return rangeFilter;
	return { _and: [filter.value, rangeFilter] };
});

const titleFieldSelection = useDisplayTemplateFieldSelection(collection, () => titleTemplate.value ?? undefined);

const dateFieldIds = computed<GlobalPrimitiveFieldId[]>(() =>
	[displayOptions.value.startDateField, displayOptions.value.endDateField]
		.filter((id): id is GlobalPrimitiveFieldId => !!id),
);
const dateFieldsSelection = useFieldSelectionForFieldIds(collection, dateFieldIds);

const queryFields = computed<FieldSelection>(() =>
	mergeFieldSelections(titleFieldSelection.value, dateFieldsSelection.value),
);

const { items, query: itemsQuery } = useItems({
	collection,
	query: () => ({ filter: mergedFilter.value, fields: queryFields.value }),
	pageSize: ref(500),
});

exposeListingQueryToBlockRootContext(() => ({
	collection: collection.value,
	query: itemsQuery.value,
}));

defineModel<CompositePrimaryKey[]>('selection', { default: () => [] });

function toCalendarTemporal(value: unknown, field: ResolvedMonospacePrimitiveField | null | undefined): Temporal.PlainDate | Temporal.ZonedDateTime | null {
	if (value == null || !field) return null;

	try {
		if (isPrimitiveTypeDateTime(field.type)) {
			if (value instanceof Temporal.ZonedDateTime) return value;
			if (value instanceof Temporal.PlainDateTime) return value.toZonedDateTime('UTC');
			const s = String(value).replace(/Z$|[+-]\d{2}:\d{2}$/, '');
			return Temporal.PlainDateTime.from(s).toZonedDateTime('UTC');
		}

		return ScheduleXPlainDate.from(String(value).slice(0, 10)) as Temporal.PlainDate;
	}
	catch {
		return null;
	}
}

const calendarData = computed(() => {
	const keyMap = new Map<string, CompositePrimaryKey>();
	const itemMap = new Map<string, AnyItem>();
	const sf = startField.value;
	if (!sf) return { events: [], keyMap, itemMap };

	const events = items.value.flatMap((item) => {
		const start = toCalendarTemporal(getItemFieldValue(item, sf), sf);
		if (!start) return [];

		const key = getItemPrimaryKey(item, collection.value);
		const id = stringifyPrimaryKey(key);
		const end = toCalendarTemporal(getItemFieldValue(item, endField.value), endField.value) ?? start;

		keyMap.set(id, key);
		itemMap.set(id, item);
		return [{ id, title: resolveDisplayTemplatePlainText(titleTemplate.value, item, collection.value) ?? getItemPrimaryKeyRouteString(item, collection.value), start, end }];
	});

	return { events, keyMap, itemMap };
});

// Our viewMode names differ from schedule-x's view ids (e.g. month <-> month-grid).
// Keep one map and derive the reverse with invert() so both directions stay in sync.
const VIEW_MODE_TO_SCHEDULE_X = {
	month: 'month-grid',
	week: 'week',
	day: 'day',
	list: 'list',
} as const satisfies Record<NonNullable<CalendarDisplayOptions['viewMode']>, ScheduleXViewId>;

const SCHEDULE_X_TO_VIEW_MODE = invert(VIEW_MODE_TO_SCHEDULE_X) as Record<ScheduleXViewId, NonNullable<CalendarDisplayOptions['viewMode']>>;

function toScheduleXView(viewMode: CalendarDisplayOptions['viewMode']): ScheduleXViewId {
	return VIEW_MODE_TO_SCHEDULE_X[viewMode ?? 'month'];
}

const currentView = ref<ScheduleXViewId>(toScheduleXView(displayOptions.value.viewMode));

const resolvedFirstDayOfWeek = computed(() => displayOptions.value.firstDayOfWeek ?? getLocaleFirstDayOfWeek());

const [DefineEventContent, ReuseEventContent] = createReusableTemplate<{ calendarEvent: { id: string | number; title?: string } }>();

const { t } = useI18n();

const dayNames = computed(() => {
	const labels = WEEKDAY_SHORT_LABEL_KEYS.map((key) => t(key));
	return resolvedFirstDayOfWeek.value === 1 ? [...labels.slice(1), labels[0]] : labels;
});

const calendarControls = shallowRef(createCalendarControlsPlugin());

// Last date shown — tells a real navigation from a view-only granularity change.
let lastSeenDate: string | undefined;

function emitDisplayOptions(patch: Partial<CalendarDisplayOptions>) {
	// Spread the RAW prop, not the `displayOptions` computed. Spreading raw keeps those
	// defaults out of the persisted saved-view payload. Same pattern as Table/Cards.
	emit('update:displayOptions', { ...props.displayOptions, ...patch });
}

function tryParseSelectedDate(value: string | undefined) {
	if (!value) return;

	try {
		return ScheduleXPlainDate.from(value);
	}
	catch {}
}

function makeCalendarApp() {
	const controls = createCalendarControlsPlugin();
	calendarControls.value = controls;

	const selectedDate = tryParseSelectedDate(displayOptions.value.currentDate);

	const app = createCalendar({
		views: [createViewMonthGrid(), createViewWeek(), createViewDay(), createViewList()],
		defaultView: currentView.value,
		firstDayOfWeek: resolvedFirstDayOfWeek.value === 0 ? 7 : 1,
		isResponsive: false,
		...(selectedDate && { selectedDate }),
		events: [],
		callbacks: {
			onRangeUpdate(range) {
				const newStart = range.start.toPlainDate().toString();
				const newEnd = range.end.toPlainDate().toString();

				if (newStart === currentViewRange.value.start && newEnd === currentViewRange.value.end) return;
				currentViewRange.value = { start: newStart, end: newEnd };

				// schedule-x swallows the first emit, so this only fires on real changes.
				const date = controls.getDate().toString();

				if (date === displayOptions.value.currentDate) {
					lastSeenDate = date;
					return;
				}

				if (date === lastSeenDate) return;

				lastSeenDate = date;
				emitDisplayOptions({ currentDate: date });
			},
			onEventClick(event) {
				const key = calendarData.value.keyMap.get(String(event.id));
				if (key) emit('open', key);
			},
		},
	}, [controls]);

	// Seed lastSeenDate from the calendar's initial date.
	lastSeenDate = controls.getDate().toString();
	return app;
}

const calendarKey = ref(0);
const calendarApp = shallowRef(makeCalendarApp());

// Schedule-X doesn't support reactively changing firstDayOfWeek after creation,
// so we recreate the app and force the calendar to remount via calendarKey.
// flush: 'sync' ensures calendarApp is reassigned before the watchEffect below reacts.
watch(
	resolvedFirstDayOfWeek,
	() => {
		calendarApp.value = makeCalendarApp();
		calendarKey.value++;
	},
	{ flush: 'sync' },
);

// Re-seed when the persisted date hydrates or changes externally (e.g. switching saved views).
function applyExternalDate(date: string) {
	const parsed = tryParseSelectedDate(date);
	if (!parsed || parsed.toString() === lastSeenDate) return;
	calendarControls.value.setDate(parsed);
}

whenever(() => displayOptions.value.currentDate, applyExternalDate);

// syncs display options panel changes
watch(
	() => displayOptions.value.viewMode,
	(viewMode) => {
		currentView.value = toScheduleXView(viewMode);
	},
);

// persist view mode changes back to display options
watch(currentView, (view) => {
	const viewMode = SCHEDULE_X_TO_VIEW_MODE[view];
	if (viewMode !== displayOptions.value.viewMode) emitDisplayOptions({ viewMode });
});

// syncs reactive event data into the calendar
watchEffect(() => {
	calendarApp.value.events.set(calendarData.value.events);
});
</script>

<template>
	<div class="flex h-full flex-col overflow-hidden">
		<div
			v-if="!startField"
			class="flex flex-1 items-center justify-center text-sm text-muted"
		>
			{{ $t('layout.calendar.no_start_date_field') }}
		</div>

		<div
			v-else
			data-calendar
			class="flex flex-1 flex-col overflow-hidden"
		>
			<LayoutCalendarHeader
				v-model="currentView"
				:range="currentViewRange"
				:calendar-controls
			/>

			<div
				v-if="currentView === 'month-grid'"
				class="flex px-px text-dimmed"
			>
				<span
					v-for="name in dayNames"
					:key="name"
					class="flex-1 py-1 text-end text-xs uppercase"
					:style="{ paddingInlineEnd: 'var(--sx-spacing-padding2)' }"
				>
					{{ name }}
				</span>
			</div>

			<DefineEventContent v-slot="{ calendarEvent }">
				<LayoutCalendarEventContent
					:calendar-event
					:item="calendarData.itemMap.get(String(calendarEvent.id))"
					:title-template
					:collection-id="collection.id"
				/>
			</DefineEventContent>

			<ClientOnly>
				<ScheduleXCalendar
					:key="calendarKey"
					:calendar-app
				>
					<template #headerContent>
						<span />
					</template>
					<template #timeGridEvent="slotProps">
						<ReuseEventContent v-bind="slotProps" />
					</template>
					<template #dateGridEvent="slotProps">
						<ReuseEventContent v-bind="slotProps" />
					</template>
					<template #monthGridEvent="slotProps">
						<ReuseEventContent v-bind="slotProps" />
					</template>
					<template #monthAgendaEvent="slotProps">
						<ReuseEventContent v-bind="slotProps" />
					</template>
				</ScheduleXCalendar>
			</ClientOnly>
		</div>
	</div>
</template>

<style scoped>
/* Some custom css is done here to give the calendar a more Monospace feel */
[data-calendar] {
	/* Primary */
	--sx-color-primary: var(--color-primary-500);
	--sx-color-on-primary: #fff;
	--sx-color-primary-container: var(--color-primary-100);
	--sx-color-on-primary-container: var(--color-primary-900);

	/* Surface & Background */
	--sx-color-surface: var(--ui-bg);
	--sx-color-surface-dim: var(--ui-bg-muted);
	--sx-color-surface-bright: var(--ui-bg-elevated);
	--sx-color-on-surface: var(--ui-text);
	--sx-color-surface-container: var(--ui-bg);
	--sx-color-surface-container-low: var(--ui-bg-muted);
	--sx-color-surface-container-high: var(--ui-bg-elevated);
	--sx-color-background: var(--ui-bg);
	--sx-color-on-background: var(--ui-text);

	/* Borders */
	--sx-color-outline: var(--ui-border-muted);
	--sx-color-outline-variant: var(--ui-border-muted);
	--sx-border: 1px solid var(--ui-border-muted);

	/* Neutral / Text */
	--sx-color-neutral: var(--ui-text);
	--sx-color-neutral-variant: var(--ui-text-dimmed);

	/* Shadow & tint */
	--sx-color-shadow: transparent;
	--sx-color-surface-tint: var(--color-primary-500);

	/* Internal overrides */
	--sx-internal-color-gray-ripple-background: var(--ui-bg-muted);
	--sx-internal-color-light-gray: var(--ui-bg-muted);
	--sx-internal-color-text: var(--ui-text);
}

[data-calendar] :deep(.sx-vue-calendar-wrapper) {
	height: 100%;
	font-family: inherit;
}

[data-calendar] :deep(.sx__calendar) {
	border: none;
	border-radius: 0;
}

[data-calendar] :deep(.sx__calendar-header) {
	display: none;
}

/* Remove Material Design ripple effect */
[data-calendar] :deep(.sx__ripple::before),
[data-calendar] :deep(.sx__ripple::after) {
	all: unset !important;
}

/* Hide built-in day names — we render our own row above the calendar */
[data-calendar] :deep(.sx__month-grid-day__header-day-name) {
	display: none;
}

/* Right-align day headers in month grid */
[data-calendar] :deep(.sx__month-grid-day__header) {
	align-items: flex-end;
	padding-inline-end: var(--sx-spacing-padding2);
}

/* Square "today" indicator instead of circle */
[data-calendar] :deep(.sx__month-grid-day__header-date),
[data-calendar] :deep(.sx__week-grid__date--is-today .sx__week-grid__date-number) {
	border-radius: var(--ui-radius);
}

/* Event modal */
[data-calendar] :deep(.sx__event-modal.sx__event-modal-default) {
	border-radius: 0.5rem;
	box-shadow: none;
	border: 1px solid var(--ui-border);
}

/* Disable Schedule-X's month slide animation */
[data-calendar] :deep(.sx__slide-left),
[data-calendar] :deep(.sx__slide-right) {
	animation: none;
}
</style>
